import React from 'react';
import { Card } from '@/components/ui/card';
import { Mail, Phone, MapPin, Linkedin, Sparkles } from 'lucide-react';
import { ensureKeyHighlightsSection } from '@/lib/reportProfile';

// CompanyResumeView — renders a candidate's company-formatted resume per the admin-defined
// resume template. Used in: HR candidate detail Resume tab AND client-side modal.

const CANONICAL_SECTION_LABELS = {
  summary: 'Profile Summary',
  key_highlights: 'Key Highlights',
  skills: 'Core Skills',
  tools_technologies: 'Key Tools & Technologies',
  experience: 'Past Companies & Experience',
  projects: 'Key Projects',
  awards: 'Awards & Recognitions',
  certifications: 'Certifications',
  education: 'Academics & Education',
  publications: 'Publications & Talks',
  languages: 'Spoken Languages',
};

/** Replace pytest-polluted labels like "Edited Summary 612e" with canonical titles. */
function displaySectionLabel(section) {
  const label = section.label || '';
  if (/^edited summary\b/i.test(label.trim())) {
    return CANONICAL_SECTION_LABELS[section.key] || label;
  }
  return label;
}

export function CompanyResumeView({ resume, template, photoUrl = null }) {
  if (!resume) return <div className="rounded-xl border bg-secondary/40 p-6 text-sm text-muted-foreground">No company resume generated yet for this candidate.</div>;
  const sections = ensureKeyHighlightsSection(template?.sections || [], resume)
    .filter((s) => s.enabled)
    .sort((a, b) => a.order - b.order);
  // Hide direct contact details (email, phone, LinkedIn) unless the template
  // explicitly opts in. Defaults to hidden so a shared/standardised CV cannot
  // be used to identify or reach the candidate directly.
  const showContact = !!template?.show_contact_details;
  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm" data-testid="company-resume-view">
      {/* Branded header */}
      <div className="relative px-7 pb-5 pt-6" style={{ background: `linear-gradient(180deg, ${template?.brand_color || '#0078D4'}0F 0%, transparent 100%)` }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">Kadel Labs · Candidate Profile</p>
            <h1 className="mt-1 text-[28px] font-semibold leading-tight tracking-tight">{resume.header?.full_name}</h1>
            <p className="text-[15px] text-foreground/80">{resume.header?.headline}</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
              {resume.header?.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{resume.header.location}</span>}
              {showContact && resume.header?.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{resume.header.email}</span>}
              {showContact && resume.header?.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{resume.header.phone}</span>}
              {showContact && resume.header?.linkedin && <span className="inline-flex items-center gap-1"><Linkedin className="h-3 w-3" />{resume.header.linkedin}</span>}
            </div>
          </div>
          {template?.show_photo && (
            <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border-2 bg-card text-lg font-semibold" style={{ borderColor: template.brand_color || '#0078D4' }}>
              {(photoUrl || resume.header?.photo_url) ? (
                <img
                  src={photoUrl || resume.header?.photo_url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                resume.header?.full_name?.split(' ').slice(0, 2).map((p) => p[0]).join('') || '?'
              )}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-5 px-7 pb-7">
        {sections.map((s) => {
          const c = SECTION_RENDERERS[s.key];
          if (!c) return null;
          const body = c(resume);
          if (!body) return null;
          return (
            <section key={s.key} data-testid={s.key === 'key_highlights' ? 'company-resume-key-highlights' : undefined}>
              <h2 className="border-b pb-1 text-[13px] font-semibold uppercase tracking-[0.14em] text-primary">{displaySectionLabel(s)}</h2>
              <div className="mt-2 text-[14px] leading-7 text-foreground/90">{body}</div>
            </section>
          );
        })}
      </div>

      <div className="flex items-center justify-between border-t bg-secondary/30 px-7 py-2 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" /> Standardised via {template?.name || 'Kadel Labs Standard'} · v{template?.version || 1}</span>
        <span>Generated {resume.generated_at}</span>
      </div>
    </div>
  );
}

const SECTION_RENDERERS = {
  summary: (r) => r.summary ? <p>{r.summary}</p> : null,
  key_highlights: (r) => {
    const items = (r.key_highlights || []).filter((h) => typeof h === 'string' && h.trim());
    if (!items.length) return null;
    return (
      <ul className="list-disc space-y-1.5 pl-5">
        {items.map((h, i) => <li key={i}>{h}</li>)}
      </ul>
    );
  },
  skills: (r) => {
    if (!r.skills) return null;
    return (
      <div className="space-y-1.5">
        {r.skills.languages?.length > 0 && <ChipRow label="Languages" items={r.skills.languages} />}
        {r.skills.platforms?.length > 0 && <ChipRow label="Platforms" items={r.skills.platforms} />}
        {r.skills.practices?.length > 0 && <ChipRow label="Practices" items={r.skills.practices} />}
      </div>
    );
  },
  tools_technologies: (r) => {
    const groups = r.tools_technologies || [];
    if (!groups.length) return null;
    return (
      <div className="space-y-1.5">
        {groups.map((g, i) => (
          (g.items?.length > 0) ? <ChipRow key={`${g.category}-${i}`} label={g.category} items={g.items} /> : null
        ))}
      </div>
    );
  },
  experience: (r) => (r.experience?.length ? (
    <ul className="space-y-3">
      {r.experience.map((x, i) => (
        <li key={i}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="font-semibold">{x.role} <span className="font-normal text-muted-foreground">· {x.company}</span></p>
            <p className="text-[12px] text-muted-foreground">{x.start} — {x.end}{x.location ? ` · ${x.location}` : ''}</p>
          </div>
          {x.bullets?.length > 0 && <ul className="mt-1 list-disc space-y-1 pl-5">{x.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
        </li>
      ))}
    </ul>
  ) : null),
  projects: (r) => (r.projects?.length ? (
    <ul className="space-y-2">
      {r.projects.map((p, i) => (
        <li key={i}>
          <p className="font-semibold">{p.name} <span className="font-normal text-muted-foreground">· {p.role} · {p.year}</span></p>
          <p>{p.summary}</p>
        </li>
      ))}
    </ul>
  ) : null),
  awards: (r) => (r.awards?.length ? (
    <ul className="space-y-2">
      {r.awards.map((a, i) => (
        <li key={i}>
          <p className="font-semibold">
            {a.title}
            {(a.issued_by || a.year) && (
              <span className="font-normal text-muted-foreground">
                {a.issued_by ? ` · ${a.issued_by}` : ''}{a.year ? ` · ${a.year}` : ''}
              </span>
            )}
          </p>
          {a.summary && <p className="text-[13px] text-muted-foreground">{a.summary}</p>}
        </li>
      ))}
    </ul>
  ) : null),
  education: (r) => (r.education?.length ? (
    <ul className="space-y-2">
      {r.education.map((e, i) => (
        <li key={i}>
          <p className="font-semibold">{e.degree} <span className="font-normal text-muted-foreground">· {e.institution} · {e.year}</span></p>
          {e.highlights && <p className="text-[13px] text-muted-foreground">{e.highlights}</p>}
        </li>
      ))}
    </ul>
  ) : null),
  certifications: (r) => (r.certifications?.length ? (
    <ul className="space-y-1">{r.certifications.map((cert, i) => <li key={i}><span className="font-semibold">{cert.name}</span> <span className="text-muted-foreground">· {cert.issued_by} · {cert.year}</span></li>)}</ul>
  ) : null),
  publications: (r) => (r.publications?.length ? (
    <ul className="space-y-1">
      {r.publications.map((p, i) => (
        <li key={i}>
          <span className="font-semibold">{p.title}</span>
          {(p.venue || p.year) && (
            <span className="text-muted-foreground">
              {p.venue ? ` · ${p.venue}` : ''}{p.year ? ` · ${p.year}` : ''}
            </span>
          )}
          {p.url && (
            <>
              {' '}
              <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">link</a>
            </>
          )}
        </li>
      ))}
    </ul>
  ) : null),
  languages: (r) => (r.languages?.length ? <p>{r.languages.join(', ')}</p> : null),
};

function ChipRow({ label, items }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-1.5">{items.map((it) => <span key={it} className="rounded-md border bg-secondary px-2 py-0.5 text-[12px] font-medium">{it}</span>)}</div>
    </div>
  );
}
